<script setup lang="ts">
import { joinURL } from 'ufo';

import type { SaleAssetBusEntity } from '~/apiClient';
import type { PlatformSaleItemEntity } from '~/types/sale.type';

// PROPS
interface Props {
    sale: PlatformSaleItemEntity;
    assetId?: string;
}

const { sale, assetId } = defineProps<Props>();

// COMPOSABLES
const { isTrustReportEligible } = useTrustReport(() => sale);
const { tt } = useTypedI18n();
const config = useRuntimeConfig();

// DATA
const asset = computed<SaleAssetBusEntity | undefined>(() => (assetId ? sale.assets.find(_a => _a.id === assetId) : sale.mainAsset));
const assigneeProfileImageUrl = computed(() => {
    const profileImage = asset.value?.assignee?.profileImage;

    return profileImage ? joinURL(config.public.cdnMediaBaseUrl, profileImage) : undefined;
});
</script>

<template>
    <div v-if="isTrustReportEligible" class="bg-primary-50 flex flex-col gap-6 rounded-xl border border-neutral-300 p-6">
        <p class="text-highlighted text-xl font-bold md:text-3xl">
            {{ asset?.brand.brandValue }} {{ asset?.model.modelValue }} <span class="text-primary">TrustReport by Fleequid</span>
        </p>
        <p>{{ tt('sdp.trustReport.description') }}</p>
        <div v-if="asset?.assignee && assigneeProfileImageUrl">
            <p class="text-sm">{{ tt('sdp.trustReport.inspectedBy') }}:</p>
            <div class="mt-4 flex items-center gap-4">
                <UAvatar
                    :src="assigneeProfileImageUrl"
                    :as="{ img: 'img' }"
                    class="size-18 shrink-0"
                    :ui="{ root: 'bg-white border border-neutral-300' }"
                />
                <p class="font-semibold">{{ asset.assignee.firstname }} {{ asset.assignee.lastname }}</p>
            </div>
        </div>
        <p v-else-if="asset?.assignee" class="text-sm">
            {{ tt('sdp.trustReport.inspectedBy') }}:
            <span class="font-bold">{{ asset.assignee.firstname }} {{ asset.assignee.lastname }}</span>
        </p>
        <slot />
    </div>

    <slot v-else />
</template>
